import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Upload, FileText, Loader2, Save, Plus, Trash2, ChevronRight, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';
import useProductCategories from '../utils/useProductCategories';
import ProductCategorySelect from '../components/ProductCategorySelect';

const API_BASE = BACKEND_BASE;

const STEP_LIBRARY = [
  "Preparer la solution",
  "Pulveriser",
  "Appliquer la solution",
  "Etaler",
  "Laisser agir",
  "Frotter",
  "Rincer",
  "Essuyer",
  "Racler",
  "Desinfecter",
  "Secher",
  "Demonter",
  "Remonter",
  "Evacuer les dechets",
  "Mettre un sac poubelle",
];

const ProductCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = new URLSearchParams(location.search).get('id');
  const isEditing = !!editId;

  const [icons, setIcons] = useState([]);
  const [methods, setMethods] = useState([]);
  const [phase, setPhase] = useState(isEditing ? 'validate' : 'upload'); // upload | analyzing | validate | saving
  const [uploading, setUploading] = useState(false);
  const [editingMethodId, setEditingMethodId] = useState(null);
  const { categories: productCategories, create: createCategory } = useProductCategories();

  const [productData, setProductData] = useState({
    name: '',
    product_type: '',
    dosage: '',
    temperature: '',
    usage: '',
    standards: '',
    product_photo: null,
    technical_sheet_file: null,
    safety_sheet_file: null,
    technical_sheet_url: '',
    safety_sheet_url: '',
    extracted_dosage_text: '',
    extracted_temperature_text: '',
    extracted_contact_time_text: '',
    extracted_usage_notes: '',
  });

  const [protocolSteps, setProtocolSteps] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/icons').then(r => setIcons(r.data)).catch(() => {}),
      api.get('/methods').then(r => setMethods(r.data)).catch(() => {}),
    ]);
  }, []);

  // Hydrate fields when editing an existing product
  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: products } = await api.get('/products');
        const p = products.find(x => x.id === editId);
        if (!p) {
          toast.error('Produit introuvable');
          navigate('/products');
          return;
        }
        if (cancelled) return;
        setProductData({
          name: p.name || '',
          product_type: p.product_type || '',
          dosage: p.dosage || '',
          temperature: p.temperature || '',
          usage: p.usage || '',
          standards: p.standards || '',
          product_photo: p.product_photo || null,
          technical_sheet_file: p.technical_sheet_file || null,
          safety_sheet_file: p.safety_sheet_file || null,
          technical_sheet_url: p.technical_sheet_url || '',
          safety_sheet_url: p.safety_sheet_url || '',
          extracted_dosage_text: p.extracted_dosage_text || '',
          extracted_temperature_text: p.extracted_temperature_text || '',
          extracted_contact_time_text: p.extracted_contact_time_text || '',
          extracted_usage_notes: p.extracted_usage_notes || '',
        });
        if (p.default_method_id) {
          setEditingMethodId(p.default_method_id);
          try {
            const { data: methodsList } = await api.get('/methods');
            const m = methodsList.find(x => x.id === p.default_method_id);
            if (m && Array.isArray(m.steps)) {
              const sorted = [...m.steps].sort((a, b) => (a.order || 0) - (b.order || 0));
              setProtocolSteps(sorted.map((s, i) => ({
                step_name: s.step_name || '',
                icon_id: s.icon_id || null,
                order: i,
                detail: s.detail || '',
              })));
            }
          } catch (e) { /* ignore */ }
        }
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || 'Erreur de chargement du produit');
      }
    })();
    return () => { cancelled = true; };
  }, [editId, isEditing, navigate]);

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/products/upload-sheet', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProductData(prev => ({ ...prev, [type]: data.url }));
      toast.success(type === 'technical_sheet_file' ? 'Fiche technique uploadee' : 'Fiche de securite uploadee');
    } catch (error) {
      toast.error('Echec de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    // Client-side validation: format + size
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Format non valide. Formats acceptes : JPG, PNG, WEBP.');
      return;
    }
    const MAX_MB = 8;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (>${MAX_MB} Mo).`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/products/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setProductData(prev => ({ ...prev, product_photo: data.url }));
      toast.success('Visuel produit mis a jour');
    } catch (error) {
      const msg = error?.response?.data?.detail || "Echec de l'upload du visuel";
      toast.error(typeof msg === 'string' ? msg : "Echec de l'upload du visuel");
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async () => {
    const hasFile = !!productData.technical_sheet_file;
    const hasUrl = !!(productData.technical_sheet_url || '').trim();
    if (!hasFile && !hasUrl) {
      toast.error('Veuillez uploader une fiche technique ou coller un lien');
      return;
    }
    if (!productData.name) {
      toast.error('Veuillez saisir le nom du produit');
      return;
    }

    setPhase('analyzing');
    setAnalysisError(null);

    try {
      const payload = hasUrl && !hasFile
        ? { sheet_url: productData.technical_sheet_url.trim() }
        : { file_url: productData.technical_sheet_file };
      const { data } = await api.post('/products/analyze-sheet', payload);

      setAnalysisResult(data);

      // Populate extracted fields
      setProductData(prev => ({
        ...prev,
        dosage: data.dosage || prev.dosage,
        temperature: data.temperature || prev.temperature,
        extracted_dosage_text: data.dosage || '',
        extracted_temperature_text: data.temperature || '',
        extracted_contact_time_text: data.contact_time || '',
        extracted_usage_notes: data.usage_notes || '',
      }));

      // Populate steps
      if (data.steps && data.steps.length > 0) {
        setProtocolSteps(data.steps.map((s, i) => ({
          step_name: s.step_name || '',
          icon_id: s.icon_id || null,
          order: i,
          detail: s.detail || '',
        })));
      }

      setPhase('validate');
      toast.success('Analyse terminee ! Verifiez et validez le protocole.');
    } catch (error) {
      const serverMsg = error?.response?.data?.detail || error?.message || '';
      const friendly = serverMsg
        ? `Erreur d'analyse : ${serverMsg}. Vous pouvez creer le protocole manuellement.`
        : 'Erreur lors de l\'analyse. Vous pouvez creer le protocole manuellement.';
      setAnalysisError(friendly);
      setPhase('validate');
      toast.error(serverMsg ? `Erreur : ${serverMsg}` : 'Erreur d\'analyse - creation manuelle possible');
    }
  };

  const addStep = (stepName) => {
    setProtocolSteps(prev => [
      ...prev,
      { step_name: stepName || '', icon_id: null, order: prev.length, detail: '' },
    ]);
  };

  const removeStep = (index) => {
    setProtocolSteps(prev => prev.filter((_, i) => i !== index));
  };

  const updateStep = (index, field, value) => {
    setProtocolSteps(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const moveStep = (index, direction) => {
    setProtocolSteps(prev => {
      const arr = [...prev];
      const newIdx = index + direction;
      if (newIdx < 0 || newIdx >= arr.length) return prev;
      [arr[index], arr[newIdx]] = [arr[newIdx], arr[index]];
      return arr;
    });
  };

  const handleSave = async () => {
    if (!productData.name) {
      toast.error('Nom du produit requis');
      return;
    }

    setPhase('saving');

    try {
      // 1. Create or update method from protocol steps
      let methodId = editingMethodId;
      const validSteps = protocolSteps.filter(s => (s.step_name || '').trim());
      if (validSteps.length > 0) {
        const methodPayload = {
          name: `Protocole ${productData.name}`,
          short_label: validSteps.map(s => s.step_name).filter(Boolean).slice(0, 3).join(' + '),
          description: `Protocole pour ${productData.name}`,
          steps: validSteps.map((s, i) => ({
            step_name: s.step_name,
            icon_id: s.icon_id,
            order: i,
          })),
        };
        if (methodId) {
          await api.put(`/methods/${methodId}`, methodPayload);
        } else {
          const { data: methodData } = await api.post('/methods', methodPayload);
          methodId = methodData.id;
        }
      }

      // 2. Create or update product
      const prodPayload = {
        name: productData.name,
        product_type: productData.product_type || null,
        dosage: productData.dosage,
        temperature: productData.temperature,
        usage: productData.usage,
        standards: productData.standards,
        product_photo: productData.product_photo,
        default_method_id: methodId,
        technical_sheet_file: productData.technical_sheet_file,
        safety_sheet_file: productData.safety_sheet_file,
        technical_sheet_url: (productData.technical_sheet_url || '').trim() || null,
        safety_sheet_url: (productData.safety_sheet_url || '').trim() || null,
        extracted_dosage_text: productData.extracted_dosage_text,
        extracted_temperature_text: productData.extracted_temperature_text,
        extracted_contact_time_text: productData.extracted_contact_time_text,
        extracted_usage_notes: productData.extracted_usage_notes,
        extraction_status: analysisResult?.extraction_status || (isEditing ? undefined : 'manual'),
      };
      if (isEditing) {
        await api.put(`/products/${editId}`, prodPayload);
        toast.success('Produit mis a jour');
      } else {
        await api.post('/products', prodPayload);
        toast.success('Produit et protocole sauvegardes !');
      }
      navigate('/products');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
      setPhase('validate');
    }
  };

  const getIconById = (id) => icons.find(i => i.id === id);

  // ==================== UPLOAD PHASE ====================
  if (phase === 'upload') {
    return (
      <Layout>
        <div data-testid="product-create-page">
          <h1 className="text-3xl font-bold mb-2" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Nouveau Produit
          </h1>
          <p className="mb-8 text-sm" style={{ color: '#6B7280' }}>
            Uploadez la fiche technique pour generer automatiquement le protocole de nettoyage.
          </p>

          <div className="bg-white rounded-lg shadow p-8 border" style={{ borderColor: '#E5E7EB' }}>
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Nom du produit *</label>
                <input type="text" value={productData.name}
                  onChange={e => setProductData({ ...productData, name: e.target.value })}
                  data-testid="product-name-input"
                  className="w-full px-4 py-2.5 border rounded-lg" style={{ borderColor: '#D1D5DB' }}
                  placeholder="ex: Prest'hyg Food Alcalinobact" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Type de produit</label>
                <ProductCategorySelect
                  value={productData.product_type}
                  onChange={(val) => setProductData({ ...productData, product_type: val || '' })}
                  categories={productCategories}
                  onCreate={createCategory}
                  dataTestId="product-category-create"
                />
              </div>
            </div>

            {/* File Uploads */}
            <div className="grid grid-cols-2 gap-6 mb-4">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                  <FileText size={16} className="inline mr-1" /> Fiche technique (PDF) *
                </label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center transition-colors relative"
                  style={{ borderColor: productData.technical_sheet_file ? '#6FCF97' : '#D1D5DB',
                    backgroundColor: productData.technical_sheet_file ? '#F0FFF4' : '#FAFAFA' }}>
                  {productData.technical_sheet_file ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle size={20} style={{ color: '#6FCF97' }} />
                      <span className="text-sm font-medium" style={{ color: '#065F46' }}>Fiche technique uploadee</span>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} style={{ color: '#9CA3AF' }} className="mx-auto mb-2" />
                      <p className="text-sm" style={{ color: '#6B7280' }}>Cliquez ou glissez le PDF ici</p>
                    </>
                  )}
                  <input type="file" accept=".pdf" data-testid="technical-sheet-input"
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    onChange={e => handleFileUpload(e.target.files[0], 'technical_sheet_file')} />
                </div>
                <label className="block text-[11px] font-medium mt-2 mb-1" style={{ color: '#6B7280' }}>Ou coller un lien web (PDF ou page)</label>
                <input type="url" placeholder="https://..."
                  data-testid="ft-url-create"
                  value={productData.technical_sheet_url}
                  onChange={e => setProductData({ ...productData, technical_sheet_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-xs" style={{ borderColor: '#D1D5DB' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                  <FileText size={16} className="inline mr-1" /> Fiche de securite (optionnel)
                </label>
                <div className="border-2 border-dashed rounded-lg p-6 text-center relative"
                  style={{ borderColor: productData.safety_sheet_file ? '#6FCF97' : '#D1D5DB',
                    backgroundColor: productData.safety_sheet_file ? '#F0FFF4' : '#FAFAFA' }}>
                  {productData.safety_sheet_file ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle size={20} style={{ color: '#6FCF97' }} />
                      <span className="text-sm font-medium" style={{ color: '#065F46' }}>Fiche de securite uploadee</span>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} style={{ color: '#9CA3AF' }} className="mx-auto mb-2" />
                      <p className="text-sm" style={{ color: '#6B7280' }}>Optionnel - PDF</p>
                    </>
                  )}
                  <input type="file" accept=".pdf" data-testid="safety-sheet-input"
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    onChange={e => handleFileUpload(e.target.files[0], 'safety_sheet_file')} />
                </div>
                <label className="block text-[11px] font-medium mt-2 mb-1" style={{ color: '#6B7280' }}>Ou coller un lien FDS</label>
                <input type="url" placeholder="https://..."
                  data-testid="fds-url-create"
                  value={productData.safety_sheet_url}
                  onChange={e => setProductData({ ...productData, safety_sheet_url: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-xs" style={{ borderColor: '#D1D5DB' }} />
              </div>
            </div>

            {/* Product Photo Upload */}
            <div className="mb-8">
              <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                <Upload size={16} className="inline mr-1" /> Photo produit (optionnel)
              </label>
              <div className="flex items-center gap-6">
                <div className="border-2 border-dashed rounded-lg p-6 text-center relative transition-colors"
                  style={{
                    borderColor: productData.product_photo ? '#6FCF97' : '#D1D5DB',
                    backgroundColor: productData.product_photo ? '#F0FFF4' : '#FAFAFA',
                    width: '280px',
                  }}>
                  {productData.product_photo ? (
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle size={20} style={{ color: '#6FCF97' }} />
                      <span className="text-sm font-medium" style={{ color: '#065F46' }}>Photo uploadee</span>
                    </div>
                  ) : (
                    <>
                      <Upload size={28} style={{ color: '#9CA3AF' }} className="mx-auto mb-2" />
                      <p className="text-sm" style={{ color: '#6B7280' }}>JPG, PNG ou WEBP</p>
                    </>
                  )}
                  <input type="file" accept=".jpg,.jpeg,.png,.webp" data-testid="product-photo-input"
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                    onChange={e => handlePhotoUpload(e.target.files[0])} />
                </div>
                {productData.product_photo && (
                  <div className="flex items-center gap-3">
                    <img src={API_BASE + productData.product_photo} alt="Photo produit"
                      className="w-20 h-20 object-contain rounded border" style={{ borderColor: '#D1D5DB' }} />
                    <button onClick={() => setProductData(prev => ({ ...prev, product_photo: null }))}
                      data-testid="remove-photo-button"
                      className="text-sm px-3 py-1 rounded" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                      <Trash2 size={14} className="inline mr-1" />Supprimer
                    </button>
                  </div>
                )}
              </div>
            </div>

            {uploading && (
              <div className="flex items-center gap-2 mb-4">
                <Loader2 size={18} className="animate-spin" style={{ color: '#4A90D9' }} />
                <span className="text-sm" style={{ color: '#4A90D9' }}>Upload en cours...</span>
              </div>
            )}

            <div className="flex gap-3">
              <button onClick={handleAnalyze}
                disabled={!productData.name || (!productData.technical_sheet_file && !(productData.technical_sheet_url || '').trim()) || uploading}
                data-testid="analyze-button"
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium disabled:opacity-50"
                style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                <FileText size={18} />
                Analyser la fiche technique
              </button>
              <button onClick={() => { setPhase('validate'); }}
                className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium border"
                style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
                Creer manuellement
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // ==================== ANALYZING PHASE ====================
  if (phase === 'analyzing') {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 size={48} className="animate-spin mb-6" style={{ color: '#0E2D43' }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: '#0E2D43' }}>Analyse en cours...</h2>
          <p className="text-sm" style={{ color: '#6B7280' }}>Extraction des informations de la fiche technique</p>
        </div>
      </Layout>
    );
  }

  // ==================== VALIDATE / EDIT PHASE ====================
  return (
    <Layout>
      <div data-testid="product-validate-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
              {isEditing ? 'Modifier le produit' : 'Validation du protocole'}
            </h1>
            <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
              {isEditing
                ? 'Modifiez les informations et le protocole du produit.'
                : 'Verifiez et corrigez les informations extraites avant de sauvegarder.'}
            </p>
          </div>
          {analysisResult?.extraction_status === 'success' && (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg" style={{ backgroundColor: '#D1FAE5' }}>
              <CheckCircle size={16} style={{ color: '#065F46' }} />
              <span className="text-sm font-medium" style={{ color: '#065F46' }}>Analyse reussie</span>
            </div>
          )}
        </div>

        {analysisError && (
          <div className="flex items-center gap-2 p-4 rounded-lg mb-6" style={{ backgroundColor: '#FEF3C7' }}>
            <AlertTriangle size={18} style={{ color: '#92400E' }} />
            <span className="text-sm" style={{ color: '#92400E' }}>{analysisError}</span>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6 mb-6">
          {/* Product Info */}
          <div className="bg-white rounded-lg shadow p-6 border col-span-1" style={{ borderColor: '#E5E7EB' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: '#0E2D43' }}>Informations produit</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Nom</label>
                <input type="text" value={productData.name}
                  onChange={e => setProductData({ ...productData, name: e.target.value })}
                  data-testid="validate-name"
                  className="w-full px-3 py-2 border rounded text-sm" style={{ borderColor: '#D1D5DB' }} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Type de produit</label>
                <ProductCategorySelect
                  value={productData.product_type}
                  onChange={(val) => setProductData({ ...productData, product_type: val || '' })}
                  categories={productCategories}
                  onCreate={createCategory}
                  dataTestId="product-category-validate"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Dosage</label>
                <input type="text" value={productData.dosage}
                  onChange={e => setProductData({ ...productData, dosage: e.target.value })}
                  data-testid="validate-dosage"
                  className="w-full px-3 py-2 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}
                  placeholder="ex: 1% ou Pret a l'emploi" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Temperature</label>
                <input type="text" value={productData.temperature}
                  onChange={e => setProductData({ ...productData, temperature: e.target.value })}
                  data-testid="validate-temperature"
                  className="w-full px-3 py-2 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}
                  placeholder="ex: 30C ou A froid" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Temps de contact</label>
                <input type="text" value={productData.extracted_contact_time_text}
                  onChange={e => setProductData({ ...productData, extracted_contact_time_text: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}
                  placeholder="ex: 5 min" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: '#6B7280' }}>Notes d&apos;utilisation</label>
                <textarea value={productData.extracted_usage_notes}
                  onChange={e => setProductData({ ...productData, extracted_usage_notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}
                  rows={3} />
              </div>

              {/* Visuel produit */}
              <div className="pt-3 border-t" style={{ borderColor: '#E5E7EB' }}>
                <label className="block text-xs font-medium mb-2" style={{ color: '#6B7280' }}>Visuel du produit</label>
                <div className="flex items-start gap-3">
                  <div
                    data-testid="product-photo-preview-box"
                    className="flex items-center justify-center rounded-lg border-2 overflow-hidden"
                    style={{
                      width: 150,
                      height: 150,
                      borderColor: productData.product_photo ? '#6FCF97' : '#E5E7EB',
                      backgroundColor: productData.product_photo ? '#F0FFF4' : '#FAFAFA',
                    }}
                  >
                    {productData.product_photo ? (
                      <img
                        src={API_BASE + productData.product_photo}
                        alt="Visuel produit actuel"
                        data-testid="current-product-photo"
                        className="max-w-full max-h-full object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1 text-center px-2">
                        <Upload size={26} style={{ color: '#9CA3AF' }} />
                        <span className="text-[11px]" style={{ color: '#6B7280' }}>Aucun visuel</span>
                      </div>
                    )}
                  </div>

                  <div className="flex-1 flex flex-col gap-2">
                    <label
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium cursor-pointer border transition-colors"
                      style={{
                        borderColor: '#0E2D43',
                        color: '#0E2D43',
                        backgroundColor: '#FFFFFF',
                      }}
                      data-testid="replace-photo-label"
                    >
                      <Upload size={14} />
                      {productData.product_photo ? 'Changer le visuel' : 'Telecharger un visuel'}
                      <input
                        type="file"
                        accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                        className="hidden"
                        data-testid="replace-photo-input"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          handlePhotoUpload(f);
                          e.target.value = '';
                        }}
                      />
                    </label>

                    {productData.product_photo && (
                      <button
                        type="button"
                        onClick={() => setProductData(prev => ({ ...prev, product_photo: null }))}
                        data-testid="remove-photo-button"
                        className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium"
                        style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}
                      >
                        <Trash2 size={14} /> Supprimer le visuel
                      </button>
                    )}

                    {uploading && (
                      <div className="flex items-center gap-2 text-xs" style={{ color: '#4A90D9' }}>
                        <Loader2 size={14} className="animate-spin" />
                        Upload en cours...
                      </div>
                    )}

                    <p className="text-[11px]" style={{ color: '#94A3B8' }}>
                      JPG, PNG ou WEBP · 8 Mo max · ratio conserve
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Protocol Steps */}
          <div className="bg-white rounded-lg shadow p-6 border col-span-2" style={{ borderColor: '#E5E7EB' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold" style={{ color: '#0E2D43' }}>Protocole de nettoyage</h3>
              <div className="flex gap-2">
                <select id="add-step-select" className="px-2 py-1 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}>
                  {STEP_LIBRARY.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => {
                  const sel = document.getElementById('add-step-select');
                  addStep(sel?.value || '');
                }}
                  data-testid="add-protocol-step"
                  className="flex items-center gap-1 px-3 py-1 rounded text-sm font-medium"
                  style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8' }}>
                  <Plus size={14} /> Ajouter
                </button>
              </div>
            </div>

            {protocolSteps.length === 0 ? (
              <div className="text-center py-8 rounded-lg" style={{ backgroundColor: '#F9FAFB' }}>
                <p className="text-sm" style={{ color: '#6B7280' }}>
                  Aucune etape. Ajoutez des etapes depuis la bibliotheque ci-dessus.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {protocolSteps.map((step, index) => {
                  const icon = step.icon_id ? getIconById(step.icon_id) : null;
                  return (
                    <div key={index} data-testid={`protocol-step-${index}`}
                      className="flex items-center gap-3 p-3 border rounded-lg"
                      style={{ borderColor: '#E5E7EB', backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                      <div className="flex flex-col gap-0.5">
                        <button type="button" onClick={() => moveStep(index, -1)} disabled={index === 0}
                          className="text-xs px-1 rounded disabled:opacity-30" style={{ color: '#0E2D43' }}>&uarr;</button>
                        <button type="button" onClick={() => moveStep(index, 1)} disabled={index === protocolSteps.length - 1}
                          className="text-xs px-1 rounded disabled:opacity-30" style={{ color: '#0E2D43' }}>&darr;</button>
                      </div>

                      <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
                        style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>{index + 1}</span>

                      {/* Step name */}
                      <select value={step.step_name}
                        onChange={e => updateStep(index, 'step_name', e.target.value)}
                        className="flex-1 px-3 py-1.5 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}>
                        <option value="">-- Etape --</option>
                        {STEP_LIBRARY.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>

                      {/* Icon selector */}
                      <select value={step.icon_id || ''}
                        onChange={e => updateStep(index, 'icon_id', e.target.value || null)}
                        className="w-40 px-2 py-1.5 border rounded text-sm" style={{ borderColor: '#D1D5DB' }}>
                        <option value="">-- Icone --</option>
                        {icons.map(ic => <option key={ic.id} value={ic.id}>{ic.name}</option>)}
                      </select>

                      {icon?.image_url && (
                        <img src={API_BASE + icon.image_url} alt={icon.name} className="w-8 h-8 object-contain" />
                      )}

                      <button onClick={() => removeStep(index)}
                        className="px-2 py-1 rounded" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Preview */}
            {protocolSteps.length > 0 && (
              <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: '#F0F9FF', borderLeft: '4px solid #0E2D43' }}>
                <p className="text-xs font-medium mb-2" style={{ color: '#0E2D43' }}>Apercu du protocole :</p>
                <div className="flex items-center gap-1 flex-wrap">
                  {protocolSteps.map((step, i) => {
                    const icon = step.icon_id ? getIconById(step.icon_id) : null;
                    return (
                      <div key={i} className="flex items-center gap-0.5">
                        {i > 0 && <ChevronRight size={12} style={{ color: '#9CA3AF' }} />}
                        <div className="flex items-center gap-1 px-2 py-1 rounded" style={{ backgroundColor: '#FFFFFF' }}>
                          {icon?.image_url && (
                            <img src={API_BASE + icon.image_url} alt="" className="w-5 h-5 object-contain" />
                          )}
                          <span className="text-xs" style={{ color: '#0E2D43' }}>{step.step_name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-3">
          <button onClick={handleSave}
            disabled={phase === 'saving' || !productData.name}
            data-testid="save-product-protocol"
            className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium disabled:opacity-50"
            style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
            {phase === 'saving' ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            {isEditing ? 'Mettre a jour' : 'Sauvegarder produit et protocole'}
          </button>
          <button onClick={() => navigate('/products')}
            className="px-6 py-3 rounded-lg font-medium border"
            style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
            Annuler
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default ProductCreate;
